// src/redux/company/companySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addCompanyAPI,
  updateCompanyAPI,
  getCompanyListAPI,
  getCompanyDetailAPI,
  archiveCompanyAPI,
  getCompanyCoachesAPI,
  removeCompanyCoachAPI,
  updateCompanyHRAPI,
  getCompanyStatsAPI,
  getCompanyActivityLogsAPI,
} from "./companiesApi";

// ✅ Add Company
export const addCompany = createAsyncThunk(
  "company/addCompany",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await addCompanyAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add company");
    }
  }
);

// ✅ Update Company
export const updateCompany = createAsyncThunk(
  "company/updateCompany",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateCompanyAPI({ id, formData });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update company");
    }
  }
);

// ✅ Get Company List
export const getCompanyList = createAsyncThunk(
  "company/getCompanyList",
  async ({ page = 1, limit = 10, search = "", is_archived = '' } = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanyListAPI({ page, limit, search, is_archived });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch companies");
    }
  }
);

// ✅ Get Company Detail
export const getCompanyById = createAsyncThunk(
  "company/getCompanyDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCompanyDetailAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company details");
    }
  }
);

// ✅ Archive/Unarchive Company
export const archiveCompany = createAsyncThunk(
  "company/archiveCompany",
  async ({ id, isArchive = true }, { rejectWithValue }) => {
    try {
      const response = await archiveCompanyAPI(id, isArchive);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive company");
    }
  }
);

// ✅ Get Coaches of a Company
export const getCompanyCoaches = createAsyncThunk(
  "company/getCompanyCoaches",
  async ({ companyId, page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await getCompanyCoachesAPI(companyId, { page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company coaches"
      );
    }
  }
);

// ✅ Remove Company Coach
export const removeCompanyCoach = createAsyncThunk(
  "company/removeCompanyCoach",
  async ({ coachId, companyId }, { rejectWithValue }) => {
    try {
      const response = await removeCompanyCoachAPI(coachId, companyId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove coach from company"
      );
    }
  }
);

// ✅ Update Company HR
export const updateCompanyHR = createAsyncThunk(
  "company/updateCompanyHR",
  async ({ companyId, hrData }, { rejectWithValue }) => {
    try {
      const response = await updateCompanyHRAPI(companyId, hrData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update HR information"
      );
    }
  }
);

// ✅ Get Company Stats
export const getCompanyStats = createAsyncThunk(
  "company/getCompanyStats",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await getCompanyStatsAPI(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company stats"
      );
    }
  }
);

// ✅ Get Company Activity Logs
export const getCompanyActivityLogs = createAsyncThunk(
  "company/getCompanyActivityLogs",
  async ({ companyId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await getCompanyActivityLogsAPI(companyId, { page, limit });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch company activity logs"
      );
    }
  }
);


// ---------- Initial State ----------
const initialState = {
  addCompany: { data: null, loading: false, error: null },
  updateCompany: { data: null, loading: false, error: null },
  getCompanyList: { data: [], total: 0, loading: false, error: null },
  getCompanyById: { data: null, loading: false, error: null },
  getCompanyCoaches: { data: [], total: 0, loading: false, error: null },
  removeCompanyCoach: { data: null, loading: false, error: null },
  archiveCompany: { data: null, loading: false, error: null },
  updateCompanyHR: { data: null, loading: false, error: null },
  getCompanyStats: { data: null, loading: false, error: null },
  getCompanyActivityLogs: { data: [], total: 0, loading: false, error: null },
};

// ---------- Slice ----------
const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    clearAddCompany: (state) => {
      state.addCompany.data = null;
      state.addCompany.error = null;
    },
    clearUpdateCompany: (state) => {
      state.updateCompany.data = null;
      state.updateCompany.error = null;
    },
    clearCompanyList: (state) => {
      state.getCompanyList.data = [];
      state.getCompanyList.error = null;
      state.getCompanyList.total = 0;
    },
    clearCompanyDetail: (state) => {
      state.getCompanyById.data = null;
      state.getCompanyById.error = null;
    },
    clearCompanyCoaches: (state) => {
      state.getCompanyCoaches.data = [];
      state.getCompanyCoaches.error = null;
      state.getCompanyCoaches.total = 0;
    },
    clearRemoveCompanyCoach: (state) => {
      state.removeCompanyCoach.data = null;
      state.removeCompanyCoach.error = null;
    },
    clearArchiveCompany: (state) => {
      state.archiveCompany.data = null;
      state.archiveCompany.error = null;
    },
    clearUpdateCompanyHR: (state) => {
      state.updateCompanyHR.data = null;
      state.updateCompanyHR.error = null;
    },
    clearCompanyStats: (state) => {
      state.getCompanyStats.data = null;
      state.getCompanyStats.error = null;
    },
    clearCompanyActivityLogs: (state) => {
      state.getCompanyActivityLogs.data = [];
      state.getCompanyActivityLogs.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---------- Add Company ----------
    builder
      .addCase(addCompany.pending, (state) => {
        state.addCompany.loading = true;
        state.addCompany.error = null;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        state.addCompany.loading = false;
        state.addCompany.data = action.payload?.data || action.payload;
      })
      .addCase(addCompany.rejected, (state, action) => {
        state.addCompany.loading = false;
        state.addCompany.error = action.payload;
      });

    // ---------- Update Company ----------
    builder
      .addCase(updateCompany.pending, (state) => {
        state.updateCompany.loading = true;
        state.updateCompany.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.updateCompany.loading = false;
        state.updateCompany.data = action.payload?.data || action.payload;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.updateCompany.loading = false;
        state.updateCompany.error = action.payload;
      });

    // ---------- Company List ----------
    builder
      .addCase(getCompanyList.pending, (state) => {
        state.getCompanyList.loading = true;
        state.getCompanyList.error = null;
      })
      .addCase(getCompanyList.fulfilled, (state, action) => {
        state.getCompanyList.loading = false;
        state.getCompanyList.data = action.payload?.data || [];
        state.getCompanyList.total = action.payload?.pagination?.total || 0;
      })
      .addCase(getCompanyList.rejected, (state, action) => {
        state.getCompanyList.loading = false;
        state.getCompanyList.error = action.payload;
      });

    // ---------- Company Detail ----------
    builder
      .addCase(getCompanyById.pending, (state) => {
        state.getCompanyById.loading = true;
        state.getCompanyById.error = null;
      })
      .addCase(getCompanyById.fulfilled, (state, action) => {
        state.getCompanyById.loading = false;
        state.getCompanyById.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyById.rejected, (state, action) => {
        state.getCompanyById.loading = false;
        state.getCompanyById.error = action.payload;
      });

    // ---------- Company Coaches ----------
    builder
      .addCase(getCompanyCoaches.pending, (state) => {
        state.getCompanyCoaches.loading = true;
        state.getCompanyCoaches.error = null;
      })
      .addCase(getCompanyCoaches.fulfilled, (state, action) => {
        state.getCompanyCoaches.loading = false;
        state.getCompanyCoaches.data = action.payload?.data || [];
        state.getCompanyCoaches.total = action.payload?.total || 0;
      })
      .addCase(getCompanyCoaches.rejected, (state, action) => {
        state.getCompanyCoaches.loading = false;
        state.getCompanyCoaches.error = action.payload;
      });

    // ---------- Remove Company Coach ----------
    builder
      .addCase(removeCompanyCoach.pending, (state) => {
        state.removeCompanyCoach.loading = true;
        state.removeCompanyCoach.error = null;
      })
      .addCase(removeCompanyCoach.fulfilled, (state, action) => {
        state.removeCompanyCoach.loading = false;
        state.removeCompanyCoach.data = action.payload?.data || action.payload;
      })
      .addCase(removeCompanyCoach.rejected, (state, action) => {
        state.removeCompanyCoach.loading = false;
        state.removeCompanyCoach.error = action.payload;
      });

    // ---------- Archive Company ----------
    builder
      .addCase(archiveCompany.pending, (state) => {
        state.archiveCompany.loading = true;
        state.archiveCompany.error = null;
      })
      .addCase(archiveCompany.fulfilled, (state, action) => {
        state.archiveCompany.loading = false;
        state.archiveCompany.data = action.payload?.data || action.payload;
      })
      .addCase(archiveCompany.rejected, (state, action) => {
        state.archiveCompany.loading = false;
        state.archiveCompany.error = action.payload;
      });

    // ---------- Update Company HR ----------
    builder
      .addCase(updateCompanyHR.pending, (state) => {
        state.updateCompanyHR.loading = true;
        state.updateCompanyHR.error = null;
      })
      .addCase(updateCompanyHR.fulfilled, (state, action) => {
        state.updateCompanyHR.loading = false;
        state.updateCompanyHR.data = action.payload?.data || action.payload;
      })
      .addCase(updateCompanyHR.rejected, (state, action) => {
        state.updateCompanyHR.loading = false;
        state.updateCompanyHR.error = action.payload;
      });

    // ---------- Get Company Stats ----------
    builder
      .addCase(getCompanyStats.pending, (state) => {
        state.getCompanyStats.loading = true;
        state.getCompanyStats.error = null;
      })
      .addCase(getCompanyStats.fulfilled, (state, action) => {
        state.getCompanyStats.loading = false;
        state.getCompanyStats.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyStats.rejected, (state, action) => {
        state.getCompanyStats.loading = false;
        state.getCompanyStats.error = action.payload;
      });

    // ---------- Get Company Activity Logs ----------
    builder
  .addCase(getCompanyActivityLogs.pending, (state) => {
    state.getCompanyActivityLogs.loading = true;
    state.getCompanyActivityLogs.error = null;
  })
  .addCase(getCompanyActivityLogs.fulfilled, (state, action) => {
    state.getCompanyActivityLogs.loading = false;
    state.getCompanyActivityLogs.data = action.payload?.data || [];
    // Adjust action.payload.pagination.total below depending on how your backend sends the total count
    state.getCompanyActivityLogs.total = action.payload?.pagination?.total || action.payload?.total || 0; 
  })
  .addCase(getCompanyActivityLogs.rejected, (state, action) => {
    state.getCompanyActivityLogs.loading = false;
    state.getCompanyActivityLogs.error = action.payload;
  });
  },
});

export const {
  clearAddCompany,
  clearUpdateCompany,
  clearCompanyList,
  clearCompanyDetail,
  clearCompanyCoaches,
  clearRemoveCompanyCoach,
  clearArchiveCompany,
  clearUpdateCompanyHR,
  clearCompanyStats,
  clearCompanyActivityLogs,
} = companySlice.actions;

export default companySlice.reducer;